import { createClient } from '@supabase/supabase-js';

// Supabase configuration - Production
const supabaseUrl = 'https://ndqjndvilkhfiinpmtoz.supabase.co';
const supabaseKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im5kcWpuZHZpbGtoZmlpbnBtdG96Iiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc0ODYxNzY2NCwiZXhwIjoyMDY0MTkzNjY0fQ.CXpNLDNZC4ER-qKczyx7cj41x0GoR-_nOo8gglPmLCA';

const supabase = createClient(supabaseUrl, supabaseKey);

// ========== TÜRKİYE SAATİ YARDIMCI FONKSİYONLAR (UTC+3) ==========
const TURKEY_OFFSET_MS = 3 * 60 * 60 * 1000; // UTC+3

/** Bugünün başlangıç ve bitiş zamanını Türkiye saatine göre UTC olarak döndürür */
function getTurkeyDayBounds(): { startOfDay: string; endOfDay: string } {
  const now = new Date();
  // Şu anki UTC zamanına +3 saat ekleyerek Türkiye zamanını bul
  const turkeyTime = new Date(now.getTime() + TURKEY_OFFSET_MS);
  // Türkiye gece yarısını UTC olarak hesapla
  const midnightTurkeyUTC = new Date(
    Date.UTC(turkeyTime.getUTCFullYear(), turkeyTime.getUTCMonth(), turkeyTime.getUTCDate()) - TURKEY_OFFSET_MS
  );
  const nextMidnightTurkeyUTC = new Date(midnightTurkeyUTC.getTime() + 24 * 60 * 60 * 1000);
  return {
    startOfDay: midnightTurkeyUTC.toISOString(),
    endOfDay: nextMidnightTurkeyUTC.toISOString()
  };
}

/** Bu ayın başlangıç ve bitiş zamanını Türkiye saatine göre UTC olarak döndürür */
function getTurkeyMonthBounds(): { startOfMonth: string; endOfMonth: string } {
  const now = new Date();
  const turkeyTime = new Date(now.getTime() + TURKEY_OFFSET_MS);
  const startOfMonthUTC = new Date(
    Date.UTC(turkeyTime.getUTCFullYear(), turkeyTime.getUTCMonth(), 1) - TURKEY_OFFSET_MS
  );
  const startOfNextMonthUTC = new Date(
    Date.UTC(turkeyTime.getUTCFullYear(), turkeyTime.getUTCMonth() + 1, 1) - TURKEY_OFFSET_MS
  );
  return {
    startOfMonth: startOfMonthUTC.toISOString(),
    endOfMonth: startOfNextMonthUTC.toISOString()
  };
}
// ===================================================================

// Fake kadın kullanıcılar için sabit profil resmi
const FAKE_FEMALE_PROFILE_PIC_URL =
  'https://firebasestorage.googleapis.com/v0/b/datezy-production.firebasestorage.app/o/profile_images%2Fkadin.webp?alt=media&token=b58d11c5-c327-4561-b49e-37148f47101c';

interface PremiumSubscription {
  id: string;
  user_id: string;
  package_type: string;
  price: number;
  start_date: string;
  end_date: string;
  is_active: boolean;
  status: string;
  payment_method: string;
  admin_note?: string;
  created_at: string;
  user?: {
    id: string;
    name: string;
    email: string;
    photos?: string[];
  };
}

export const supabaseService = {
  // Get all premium subscriptions with user info
  async getAllPremiumSubscriptions({ limit = 50, offset = 0 }: { limit?: number; offset?: number } = {}): Promise<PremiumSubscription[]> {
    try {
      console.log('🔍 Supabase service: Starting getAllPremiumSubscriptions...');
      console.log('📋 Query params:', { limit, offset });

      // İlk olarak premium subscriptions'ları al
      const { data: premiumData, error: premiumError } = await supabase
        .from('premium_subscriptions')
        .select('*')
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      console.log('📊 Raw premium query result:', { 
        data: premiumData, 
        error: premiumError,
        dataLength: premiumData?.length || 0 
      });

      if (premiumError) {
        console.error('❌ Error fetching premium subscriptions:', premiumError);
        return [];
      }

      if (!premiumData || premiumData.length === 0) {
        console.log('⚠️ No premium data found in database');
        return [];
      }

      // User ID'leri topla
      const userIds = Array.from(new Set(premiumData.map(p => p.user_id)));
      console.log('👥 User IDs to fetch:', userIds);
      
      // Users'ları ayrı olarak fetch et (varsa)
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id, name, email, profile_pic_url')
        .in('id', userIds);

      console.log('👤 Users query result:', { 
        data: usersData, 
        error: usersError,
        dataLength: usersData?.length || 0 
      });

      if (usersError) {
        console.warn('⚠️ Error fetching users (some may not exist):', usersError);
      }

      // User bilgilerini map'e çevir
      const usersMap = new Map();
      if (usersData) {
        usersData.forEach(user => {
          usersMap.set(user.id, {
            id: user.id,
            name: user.name || 'Bilinmeyen',
            email: user.email || '',
            photos: user.profile_pic_url ? [user.profile_pic_url] : []
          });
        });
      }

      // Premium subscription ve user bilgilerini birleştir
      const result = premiumData.map(premium => ({
        ...premium,
        user: usersMap.get(premium.user_id) || {
          id: premium.user_id,
          name: 'Bilinmeyen Kullanıcı',
          email: premium.user_id,
          photos: []
        }
      }));

      console.log('✅ Final result:', result);
      return result;
    } catch (error) {
      console.error('❌ Error in getAllPremiumSubscriptions:', error);
      return [];
    }
  },

  // Create a new premium subscription
  async createPremiumSubscription({
    userId,
    packageType,
    price,
    durationDays,
    paymentMethod = 'admin_grant',
    productId,
    purchaseToken,
    adminNote,
    metadata = {},
  }: {
    userId: string;
    packageType: string;
    price: number;
    durationDays: number;
    paymentMethod?: string;
    productId?: string;
    purchaseToken?: string;
    adminNote?: string;
    metadata?: any;
  }): Promise<boolean> {
    try {
      const premiumId = `premium_${userId}_${Date.now()}`;
      const now = new Date();
      const endDate = new Date(now.getTime() + durationDays * 24 * 60 * 60 * 1000);

      const premiumData = {
        id: premiumId,
        user_id: userId,
        package_type: packageType,
        price,
        start_date: now.toISOString(),
        end_date: endDate.toISOString(),
        is_active: true,
        status: 'active',
        payment_method: paymentMethod,
        product_id: productId,
        purchase_token: purchaseToken,
        admin_note: adminNote,
        metadata,
        created_at: now.toISOString(),
        updated_at: now.toISOString(),
      };

      const { error } = await supabase
        .from('premium_subscriptions')
        .insert(premiumData);

      if (error) {
        console.error('Error creating premium subscription:', error);
        return false;
      }

      console.log('✅ Premium subscription created:', premiumId);
      return true;
    } catch (error) {
      console.error('Error in createPremiumSubscription:', error);
      return false;
    }
  },

  // Cancel premium subscription
  async cancelPremiumSubscription(premiumId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('premium_subscriptions')
        .update({
          is_active: false,
          status: 'cancelled',
          cancelled_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', premiumId);

      if (error) {
        console.error('Error cancelling premium subscription:', error);
        return false;
      }

      console.log('✅ Premium subscription cancelled:', premiumId);
      return true;
    } catch (error) {
      console.error('Error in cancelPremiumSubscription:', error);
      return false;
    }
  },

  // Get user's premium info
  async getUserPremiumInfo(userId: string): Promise<any> {
    try {
      const { data, error } = await supabase
        .from('premium_subscriptions')
        .select('*')
        .eq('user_id', userId)
        .eq('is_active', true)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) {
        console.error('Error getting premium info:', error);
        return null;
      }

      if (data && data.length > 0) {
        const premium = data[0];
        const endDate = new Date(premium.end_date);
        
        // Check if still valid
        if (endDate > new Date()) {
          return {
            id: premium.id,
            is_premium: true,
            package_type: premium.package_type,
            price: premium.price,
            start_date: premium.start_date,
            end_date: premium.end_date,
            days_remaining: Math.ceil((endDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)),
            payment_method: premium.payment_method,
            admin_note: premium.admin_note,
          };
        }
      }

      return {
        is_premium: false,
        package_type: null,
        price: 0,
        start_date: null,
        end_date: null,
        days_remaining: 0,
      };
    } catch (error) {
      console.error('Error in getUserPremiumInfo:', error);
      return null;
    }
  },

  // Check if user is premium
  async isPremiumUser(userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('premium_subscriptions')
        .select('id, end_date')
        .eq('user_id', userId)
        .eq('is_active', true)
        .eq('status', 'active')
        .limit(1);

      if (error) {
        console.error('Error checking premium status:', error);
        return false;
      }

      if (data && data.length > 0) {
        const endDate = new Date(data[0].end_date);
        return endDate > new Date();
      }

      return false;
    } catch (error) {
      console.error('Error in isPremiumUser:', error);
      return false;
    }
  },

  // Get active premium count
  async getActivePremiumCount(): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('premium_subscriptions')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true)
        .eq('status', 'active');

      if (error) {
        console.error('Error getting active premium count:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('Error in getActivePremiumCount:', error);
      return 0;
    }
  },

  // Health check
  async isHealthy(): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('users')
        .select('id')
        .limit(1);

      return !error;
    } catch (error) {
      console.error('Supabase health check failed:', error);
      return false;
    }
  },

  // REPORTS OPERATIONS

  // Get all reports
  async getAllReports({ 
    limit = 50, 
    offset = 0, 
    statusFilter 
  }: { 
    limit?: number; 
    offset?: number; 
    statusFilter?: string; 
  } = {}): Promise<any[]> {
    try {
      let query = supabase
        .from('reports')
        .select(`
          id,
          reporter_id,
          reported_user_id,
          reason,
          description,
          status,
          timestamp,
          admin_notes,
          created_at,
          updated_at
        `)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (statusFilter) {
        query = query.eq('status', statusFilter);
      }

      const { data: reportsData, error } = await query;

      if (error) {
        console.error('Error getting reports:', error);
        return [];
      }

      if (!reportsData || reportsData.length === 0) {
        return [];
      }

      // Get unique user IDs
      const userIds = Array.from(new Set([
        ...reportsData.map(r => r.reporter_id),
        ...reportsData.map(r => r.reported_user_id)
      ].filter(Boolean)));

      // Fetch user details
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id, name, email, photos')
        .in('id', userIds);

      if (usersError) {
        console.warn('Error fetching users for reports:', usersError);
      }

      // Create users map
      const usersMap = new Map();
      if (usersData) {
        usersData.forEach(user => {
          usersMap.set(user.id, user);
        });
      }

      // Combine reports with user data
      const result = reportsData.map(report => ({
        ...report,
        reporter: usersMap.get(report.reporter_id) || {
          id: report.reporter_id,
          name: 'Unknown User',
          email: 'unknown@datezy.com',
          photos: []
        },
        reported_user: usersMap.get(report.reported_user_id) || {
          id: report.reported_user_id,
          name: 'Unknown User',
          email: 'unknown@datezy.com',
          photos: []
        }
      }));

      return result;
    } catch (error) {
      console.error('Error in getAllReports:', error);
      return [];
    }
  },

  // Update report status
  async updateReportStatus(
    reportId: string, 
    status: string, 
    adminNotes?: string
  ): Promise<boolean> {
    try {
      const updateData: any = {
        status,
        updated_at: new Date().toISOString(),
      };

      if (adminNotes) {
        updateData.admin_notes = adminNotes;
      }

      const { error } = await supabase
        .from('reports')
        .update(updateData)
        .eq('id', reportId);

      if (error) {
        console.error('Error updating report status:', error);
        return false;
      }

      console.log('✅ Report status updated:', reportId);
      return true;
    } catch (error) {
      console.error('Error in updateReportStatus:', error);
      return false;
    }
  },

  // Delete report
  async deleteReport(reportId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('reports')
        .delete()
        .eq('id', reportId);

      if (error) {
        console.error('Error deleting report:', error);
        return false;
      }

      console.log('✅ Report deleted:', reportId);
      return true;
    } catch (error) {
      console.error('Error in deleteReport:', error);
      return false;
    }
  },

  // Get reports statistics
  async getReportsStatistics(): Promise<{
    total: number;
    pending: number;
    reviewing: number;
    resolved: number;
    dismissed: number;
  }> {
    try {
      const { data, error } = await supabase
        .from('reports')
        .select('status');

      if (error) {
        console.error('Error getting reports statistics:', error);
        return {
          total: 0,
          pending: 0,
          reviewing: 0,
          resolved: 0,
          dismissed: 0,
        };
      }

      const stats = {
        total: data?.length || 0,
        pending: 0,
        reviewing: 0,
        resolved: 0,
        dismissed: 0,
      };

      if (data) {
        data.forEach(report => {
          const status = report.status || 'pending';
          if (stats.hasOwnProperty(status)) {
            stats[status as keyof typeof stats]++;
          }
        });
      }

      return stats;
    } catch (error) {
      console.error('Error in getReportsStatistics:', error);
      return {
        total: 0,
        pending: 0,
        reviewing: 0,
        resolved: 0,
        dismissed: 0,
      };
    }
  },

  // === POST REPORTS ===
  
  // Get all post reports
  async getAllPostReports({ 
    limit = 50, 
    offset = 0, 
    statusFilter 
  }: { 
    limit?: number; 
    offset?: number; 
    statusFilter?: string; 
  } = {}): Promise<any[]> {
    try {
      console.log('🔍 Supabase service: Starting getAllPostReports...');
      console.log('📋 Query params:', { limit, offset, statusFilter });

      let query = supabase
        .from('post_reports')
        .select(`
          id,
          reporter_user_id,
          reported_user_id,
          post_id,
          report_reason,
          report_category,
          post_content_snapshot,
          post_image_snapshot,
          priority,
          moderator_id,
          moderated_at,
          report_status,
          moderator_notes,
          created_at,
          reported_at
        `)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      // Status filter uygula
      if (statusFilter && statusFilter !== 'all' && statusFilter !== '') {
        query = query.eq('report_status', statusFilter);
      }

      const { data: reportsData, error } = await query;

      console.log('📊 Post reports query result:', { 
        data: reportsData, 
        error: error,
        dataLength: reportsData?.length || 0 
      });

      if (error) {
        console.error('❌ Error fetching post reports:', error);
        return [];
      }

      if (!reportsData || reportsData.length === 0) {
        return [];
      }

      // Get unique user IDs
      const userIds = Array.from(new Set([
        ...reportsData.map(r => r.reporter_user_id),
        ...reportsData.map(r => r.reported_user_id),
        ...reportsData.map(r => r.moderator_id)
      ].filter(Boolean)));

      // Fetch user details
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id, name, email, photos')
        .in('id', userIds);

      if (usersError) {
        console.warn('⚠️ Error fetching users for post reports:', usersError);
      }

      // Create users map
      const usersMap = new Map();
      if (usersData) {
        usersData.forEach(user => {
          usersMap.set(user.id, user);
        });
      }

      // Combine reports with user data and map to expected format
      const result = reportsData.map(report => ({
        ...report,
        // Map database fields to expected UI fields for backward compatibility
        reason: report.report_reason || report.report_category,
        description: report.report_category,
        content_snapshot: report.post_content_snapshot,
        admin_notes: report.moderator_notes,
        updated_at: report.moderated_at || report.created_at,
        priority: report.priority ? (typeof report.priority === 'string' ? parseInt(report.priority) || 1 : report.priority) : 1,
        reporter: usersMap.get(report.reporter_user_id) || {
          id: report.reporter_user_id,
          name: 'Unknown User',
          email: 'unknown@datezy.com',
          photos: []
        },
        reported_user: usersMap.get(report.reported_user_id) || {
          id: report.reported_user_id,
          name: 'Unknown User',
          email: 'unknown@datezy.com',
          photos: []
        },
        moderator: report.moderator_id ? (usersMap.get(report.moderator_id) || {
          id: report.moderator_id,
          name: 'Unknown Moderator',
          email: 'unknown@datezy.com'
        }) : null
      }));

      console.log('✅ Final post reports result count:', result.length);
      return result;
    } catch (error) {
      console.error('❌ Error in getAllPostReports:', error);
      return [];
    }
  },

  // Get post reports statistics
  async getPostReportsStatistics(): Promise<{
    total: number;
    pending: number;
    under_review: number;
    resolved: number;
    dismissed: number;
  }> {
    try {
      console.log('📊 Getting post reports statistics...');

      const { data, error } = await supabase
        .from('post_reports')
        .select('report_status');

      if (error) {
        console.error('❌ Error fetching post reports statistics:', error);
        return { total: 0, pending: 0, under_review: 0, resolved: 0, dismissed: 0 };
      }

      const stats = {
        total: data?.length || 0,
        pending: 0,
        under_review: 0,
        resolved: 0,
        dismissed: 0,
      };

      data?.forEach((report: any) => {
        switch (report.report_status) {
          case 'pending':
            stats.pending++;
            break;
          case 'reviewing':
            stats.under_review++;
            break;
          case 'resolved':
            stats.resolved++;
            break;
          case 'dismissed':
            stats.dismissed++;
            break;
        }
      });

      console.log('📈 Post reports statistics:', stats);
      return stats;
    } catch (error) {
      console.error('❌ Error in getPostReportsStatistics:', error);
      return { total: 0, pending: 0, under_review: 0, resolved: 0, dismissed: 0 };
    }
  },

  // Update post report status
  async updatePostReportStatus(
    reportId: string, 
    status: string, 
    adminNotes?: string,
    moderatorId?: string
  ): Promise<boolean> {
    try {
      console.log('🔄 Updating post report status:', { reportId, status, adminNotes });

      const updateData: any = {
        report_status: status,
        moderated_at: new Date().toISOString(),
      };

      if (adminNotes) {
        updateData.moderator_notes = adminNotes;
      }

      if (moderatorId) {
        updateData.moderator_id = moderatorId;
      }

      const { error } = await supabase
        .from('post_reports')
        .update(updateData)
        .eq('id', reportId);

      if (error) {
        console.error('❌ Error updating post report status:', error);
        return false;
      }

      console.log('✅ Post report status updated successfully');
      return true;
    } catch (error) {
      console.error('❌ Error in updatePostReportStatus:', error);
      return false;
    }
  },

  // Delete post report
  async deletePostReport(reportId: string): Promise<boolean> {
    try {
      console.log('🗑️ Deleting post report:', reportId);

      const { error } = await supabase
        .from('post_reports')
        .delete()
        .eq('id', reportId);

      if (error) {
        console.error('❌ Error deleting post report:', error);
        return false;
      }

      console.log('✅ Post report deleted successfully');
      return true;
    } catch (error) {
      console.error('❌ Error in deletePostReport:', error);
      return false;
    }
  },

  // Test function for post reports
  async testPostReports(): Promise<void> {
    console.log('🧪 Testing post reports functionality...');
    try {
      const reports = await this.getAllPostReports({ limit: 5 });
      console.log('✅ Test successful:', reports.length, 'reports found');
      
      const stats = await this.getPostReportsStatistics();
      console.log('✅ Statistics test successful:', stats);
    } catch (error) {
      console.error('❌ Test failed:', error);
    }
  },

  // Delete specific post from reports
  async deleteReportedPost(postId: string): Promise<boolean> {
    try {
      console.log('🗑️ Deleting reported post:', postId);

      const { error } = await supabase
        .from('posts')
        .update({ 
          is_deleted: true,
          deleted_at: new Date().toISOString(),
          deleted_reason: 'Admin deletion from post report'
        })
        .eq('id', postId);

      if (error) {
        console.error('❌ Error deleting reported post:', error);
        return false;
      }

      console.log('✅ Reported post deleted successfully');
      return true;
    } catch (error) {
      console.error('❌ Error in deleteReportedPost:', error);
      return false;
    }
  },

  // 🔄 HYBRID POSTS OPERATIONS

  // Create a new post to Supabase (hybrid system)
  async createPost({
    id,
    userId,
    userName,
    userProfilePicUrl,
    content,
    imageUrls,
    createdAt,
  }: {
    id: string;
    userId: string;
    userName: string;
    userProfilePicUrl?: string;
    content: string;
    imageUrls?: string[];
    createdAt: Date;
  }): Promise<boolean> {
    try {
      const expiresAt = new Date(createdAt.getTime() + 90 * 24 * 60 * 60 * 1000); // 3 ay sonra

      const postData = {
        id,
        user_id: userId,
        user_name: userName,
        user_profile_pic_url: userProfilePicUrl,
        content,
        image_urls: imageUrls,
        like_count: 0,
        comment_count: 0,
        liked_by_user_ids: [],
        is_deleted: false,
        created_at: createdAt.toISOString(),
        expires_at: expiresAt.toISOString(),
      };

      const { error } = await supabase
        .from('posts')
        .insert(postData);

      if (error) {
        console.error('❌ Supabase createPost error:', error);
        return false;
      }

      console.log('✅ Post created in Supabase:', id);
      return true;
    } catch (error) {
      console.error('❌ Error in createPost:', error);
      return false;
    }
  },

  // Get all posts from Supabase
  async getAllPosts({ limit = 50, offset = 0 }: { limit?: number; offset?: number } = {}): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('posts')
        .select('*')
        .eq('is_deleted', false)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        console.error('❌ Error fetching posts:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('❌ Error in getAllPosts:', error);
      return [];
    }
  },

  // Get users from Supabase for posts creation
  async getAllUsers({ limit = 50 }: { limit?: number } = {}): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, name, profilePicUrl:profile_pic_url')
        .limit(limit);

      if (error) {
        console.error('❌ Error fetching users:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('❌ Error in getAllUsers:', error);
      return [];
    }
  },

  // Delete all posts from Supabase
  async deleteAllPosts(): Promise<number> {
    try {
      // Önce post sayısını al
      const { count } = await supabase
        .from('posts')
        .select('*', { count: 'exact', head: true });

      // Tüm posts'ları is_deleted = true yap
      const { error } = await supabase
        .from('posts')
        .update({ is_deleted: true })
        .eq('is_deleted', false);

      if (error) {
        console.error('❌ Error deleting posts:', error);
        return 0;
      }

      console.log(`✅ ${count || 0} posts marked as deleted in Supabase`);
      return count || 0;
    } catch (error) {
      console.error('❌ Error in deleteAllPosts:', error);
      return 0;
    }
  },

  // 🔄 HYBRID DASHBOARD STATISTICS

  // Get total users count from Supabase
  async getTotalUsersCount(): Promise<number> {
    try {
      console.log('🔍 Supabase: Toplam kullanıcı sayısı alınıyor...');
      
      const { count, error } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true });

      if (error) {
        console.error('❌ Supabase getTotalUsersCount error:', error);
        throw error;
      }

      console.log('✅ Supabase toplam kullanıcı sayısı:', count);
      return count || 0;
    } catch (error) {
      console.error('❌ Error in getTotalUsersCount:', error);
      throw error;
    }
  },

  // Get active users count from Supabase
  async getActiveUsersCount(): Promise<number> {
    try {
      console.log('🔍 Supabase: Aktif kullanıcı sayısı alınıyor...');
      
      // isActive field'ına göre aktif kullanıcıları say
      const { count, error } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true);

      if (error) {
        console.error('❌ Supabase getActiveUsersCount error:', error);
        throw error;
      }

      console.log('✅ Supabase aktif kullanıcı sayısı:', count);
      return count || 0;
    } catch (error) {
      console.error('❌ Error in getActiveUsersCount:', error);
      throw error;
    }
  },

  // Get today's new users count from Supabase
  async getTodayNewUsersCount(): Promise<number> {
    try {
      console.log('🔍 Supabase: Bugünkü yeni kullanıcı sayısı alınıyor (TR saati)...');
      
      const { startOfDay, endOfDay } = getTurkeyDayBounds();
      
      const { count, error } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startOfDay)
        .lt('created_at', endOfDay);

      if (error) {
        console.error('❌ Supabase getTodayNewUsersCount error:', error);
        throw error;
      }

      console.log('✅ Supabase bugünkü yeni kullanıcı sayısı:', count);
      return count || 0;
    } catch (error) {
      console.error('❌ Error in getTodayNewUsersCount:', error);
      throw error;
    }
  },

  // Get today's new users count by gender (Kadın/Erkek) from Supabase
  async getTodayNewUsersCountByGender(): Promise<{ female: number; male: number; unknown: number }> {
    try {
      console.log('🔍 Supabase: Bugünkü yeni kullanıcı sayısı (cinsiyete göre, TR saati) alınıyor...');
      
      const { startOfDay, endOfDay } = getTurkeyDayBounds();
      
      const { data, error } = await supabase
        .from('users')
        .select('gender')
        .gte('created_at', startOfDay)
        .lt('created_at', endOfDay);

      if (error) {
        console.error('❌ Supabase getTodayNewUsersCountByGender error:', error);
        throw error;
      }

      let female = 0;
      let male = 0;
      let unknown = 0;

      (data || []).forEach((row: any) => {
        const g = (row?.gender ?? '').toString().toLowerCase();
        if (g === 'kadın' || g === 'kadin' || g === 'female') female += 1;
        else if (g === 'erkek' || g === 'male') male += 1;
        else unknown += 1;
      });

      console.log('✅ Supabase bugünkü yeni üyeler (cinsiyet):', { female, male, unknown });
      return { female, male, unknown };
    } catch (error) {
      console.error('❌ Error in getTodayNewUsersCountByGender:', error);
      throw error;
    }
  },

  // Get premium users count from Supabase
  async getPremiumUsersCount(): Promise<number> {
    try {
      console.log('🔍 Supabase: Premium kullanıcı sayısı alınıyor...');
      
      const { count, error } = await supabase
        .from('premium_subscriptions')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true);

      if (error) {
        console.error('❌ Supabase getPremiumUsersCount error:', error);
        throw error;
      }

      console.log('✅ Supabase premium kullanıcı sayısı:', count);
      return count || 0;
    } catch (error) {
      console.error('❌ Error in getPremiumUsersCount:', error);
      throw error;
    }
  },

  // Get recent users from Supabase
  async getRecentUsers(count: number = 5): Promise<any[]> {
    try {
      console.log('🔍 Supabase: Son kullanıcılar alınıyor...');
      
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(count);

      if (error) {
        console.error('❌ Supabase getRecentUsers error:', error);
        throw error;
      }

      // Field mapping yaparak Firebase formatına çevir
      const mappedUsers = (data || []).map(user => {
        // Birth date'den yaş hesapla
        let age = null;
        if (user.birth_date) {
          try {
            const birthDate = new Date(user.birth_date);
            const today = new Date();
            age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
              age--;
            }
          } catch (error) {
            console.warn('Birth date parse error for user:', user.id, error);
          }
        }

        // Photos array'ini parse et
        let parsedPhotos: string[] = [];
        try {
          if (Array.isArray(user.photos)) {
            parsedPhotos = user.photos.filter((p: string) => p && p.trim() !== '');
          } else if (typeof user.photos === 'string' && user.photos.trim()) {
            parsedPhotos = JSON.parse(user.photos).filter((p: string) => p && p.trim() !== '');
          }
        } catch (error) {
          console.warn('Photos parse error for user:', user.id, error);
        }

        // Profil resmi için fallback alanları
        const profilePicUrl = user.profile_pic_url || user.profilePicUrl || user.profile_picture_url || user.profilePictureUrl || null;
        const googlePhotoURL = user.google_photo_url || user.googlePhotoURL || user.photoURL || null;
        
        return {
          id: user.id,
          email: user.email,
          name: user.name,
          age: age,
          gender: user.gender,
          isActive: user.is_active,
          createdAt: user.created_at,
          lastActiveAt: user.last_active,
          photos: parsedPhotos,
          // Ek profil resmi alanları
          profilePicUrl: profilePicUrl,
          googlePhotoURL: googlePhotoURL,
          photoURL: googlePhotoURL,
          bio: user.bio,
          interests: (() => {
            try {
              // Eğer zaten array ise, direkt kullan
              if (Array.isArray(user.interests)) return user.interests;
              // Eğer string ise, parse et
              if (typeof user.interests === 'string') return JSON.parse(user.interests);
              // Diğer durumlarda boş array
              return [];
            } catch (error) {
              console.warn('Interests parse error for user:', user.id, error);
              return [];
            }
          })(),
          relationshipStatus: user.relationship_status,
          languages: (() => {
            try {
              // Eğer zaten array ise, direkt kullan
              if (Array.isArray(user.languages)) return user.languages;
              // Eğer string ise, parse et
              if (typeof user.languages === 'string') return JSON.parse(user.languages);
              // Diğer durumlarda boş array
              return [];
            } catch (error) {
              console.warn('Languages parse error for user:', user.id, error);
              return [];
            }
          })(),
        };
      });

      console.log('✅ Supabase son kullanıcılar:', mappedUsers.length || 0);
      return mappedUsers;
    } catch (error) {
      console.error('❌ Error in getRecentUsers:', error);
      throw error;
    }
  },

  // Get total matches count from Supabase
  async getTotalMatchesCount(): Promise<number> {
    try {
      console.log('🔍 Supabase: Toplam eşleşme sayısı alınıyor...');
      
      const { count, error } = await supabase
        .from('matches')
        .select('*', { count: 'exact', head: true });

      if (error) {
        console.error('❌ Supabase getTotalMatchesCount error:', error);
        throw error;
      }

      console.log('✅ Supabase toplam eşleşme sayısı:', count);
      return count || 0;
    } catch (error) {
      console.error('❌ Error in getTotalMatchesCount:', error);
      throw error;
    }
  },

  // 🔄 HYBRID REVENUE STATISTICS

  // Get daily revenue filtered by product_type
  async getDailyRevenueByProductType(productType: string): Promise<{ revenue: number; count: number }> {
    try {
      console.log('🔍 Supabase: Günlük gelir (product_type filtresi, TR saati) alınıyor...', productType);
      
      const { startOfDay, endOfDay } = getTurkeyDayBounds();
      
      const { data, error } = await supabase
        .from('sales')
        .select('amount')
        .eq('status', 'completed')
        .eq('product_type', productType)
        .gte('created_at', startOfDay)
        .lt('created_at', endOfDay);

      if (error) {
        console.error('❌ Supabase getDailyRevenueByProductType error:', error);
        throw error;
      }

      const revenue = data?.reduce((total, sale) => total + (sale.amount || 0), 0) || 0;
      const count = data?.length || 0;
      console.log(`✅ Supabase günlük gelir (${productType}):`, { revenue, count });
      return { revenue, count };
    } catch (error) {
      console.error('❌ Error in getDailyRevenueByProductType:', error);
      throw error;
    }
  },

  // Get today's new premium subscribers count
  async getTodayPremiumSubscribersCount(): Promise<number> {
    try {
      console.log('🔍 Supabase: Bugün abone olan sayısı alınıyor (TR saati)...');
      
      const { startOfDay, endOfDay } = getTurkeyDayBounds();
      
      const { count, error } = await supabase
        .from('sales')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'completed')
        .in('product_type', ['premium_subscription', 'premium_plus_subscription'])
        .gte('created_at', startOfDay)
        .lt('created_at', endOfDay);

      if (error) {
        console.error('❌ Supabase getTodayPremiumSubscribersCount error:', error);
        throw error;
      }

      console.log('✅ Supabase bugün abone olan:', count);
      return count || 0;
    } catch (error) {
      console.error('❌ Error in getTodayPremiumSubscribersCount:', error);
      throw error;
    }
  },

  // Get daily revenue from Supabase
  async getDailyRevenue(): Promise<number> {
    try {
      console.log('🔍 Supabase: Günlük gelir alınıyor (TR saati)...');
      
      const { startOfDay, endOfDay } = getTurkeyDayBounds();
      
      const { data, error } = await supabase
        .from('sales')
        .select('amount')
        .eq('status', 'completed')
        .gte('created_at', startOfDay)
        .lt('created_at', endOfDay);

      if (error) {
        console.error('❌ Supabase getDailyRevenue error:', error);
        throw error;
      }

      const dailyRevenue = data?.reduce((total, sale) => total + (sale.amount || 0), 0) || 0;
      console.log('✅ Supabase günlük gelir (sales tablosundan):', dailyRevenue);
      return dailyRevenue;
    } catch (error) {
      console.error('❌ Error in getDailyRevenue:', error);
      throw error;
    }
  },

  // Get monthly revenue from Supabase
  async getMonthlyRevenue(): Promise<number> {
    try {
      console.log('🔍 Supabase: Aylık gelir alınıyor (TR saati)...');
      
      const { startOfMonth, endOfMonth } = getTurkeyMonthBounds();
      
      const { data, error } = await supabase
        .from('sales')
        .select('amount')
        .eq('status', 'completed')
        .gte('created_at', startOfMonth)
        .lt('created_at', endOfMonth);

      if (error) {
        console.error('❌ Supabase getMonthlyRevenue error:', error);
        throw error;
      }

      const monthlyRevenue = data?.reduce((total, sale) => total + (sale.amount || 0), 0) || 0;
      console.log('✅ Supabase aylık gelir (sales tablosundan):', monthlyRevenue);
      return monthlyRevenue;
    } catch (error) {
      console.error('❌ Error in getMonthlyRevenue:', error);
      throw error;
    }
  },

  // Get total revenue from Supabase
  async getTotalRevenue(): Promise<number> {
    try {
      console.log('🔍 Supabase: Toplam gelir alınıyor...');
      
      const { data, error } = await supabase
        .from('sales')
        .select('amount')
        .eq('status', 'completed');

      if (error) {
        console.error('❌ Supabase getTotalRevenue error:', error);
        throw error;
      }

      const totalRevenue = data?.reduce((total, sale) => total + (sale.amount || 0), 0) || 0;
      console.log('✅ Supabase toplam gelir (sales tablosundan):', totalRevenue);
      return totalRevenue;
    } catch (error) {
      console.error('❌ Error in getTotalRevenue:', error);
      throw error;
    }
  },

  // Get pending gift card applications count
  async getPendingGiftCardApplications(): Promise<number> {
    try {
      console.log('🔍 Supabase: Bekleyen hediye kartı başvuruları sayılıyor...');
      
      const { count, error } = await supabase
        .from('gift_card_applications')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      if (error) {
        console.error('❌ Supabase getPendingGiftCardApplications error:', error);
        throw error;
      }

      console.log('✅ Supabase bekleyen hediye kartı başvuruları:', count);
      return count || 0;
    } catch (error) {
      console.error('❌ Error in getPendingGiftCardApplications:', error);
      throw error;
    }
  },

  // 🔄 USER MANAGEMENT OPERATIONS

  // Get all users with pagination and filters for user management
  async getAllUsersWithPagination({ 
    limit = 50, 
    offset = 0, 
    searchQuery,
    statusFilter = 'all',
    genderFilter = 'all'
  }: { 
    limit?: number; 
    offset?: number; 
    searchQuery?: string;
    statusFilter?: string;
    genderFilter?: string;
  } = {}): Promise<{ users: any[], total: number }> {
    try {
      let query = supabase
        .from('users')
        .select('*');

      // Status filter
      if (statusFilter === 'active') {
        query = query.eq('is_active', true);
      } else if (statusFilter === 'inactive') {
        query = query.eq('is_active', false);
      }

      // Gender filter
      if (genderFilter !== 'all') {
        if (genderFilter === 'male') {
          query = query.in('gender', ['Erkek', 'male']);
        } else if (genderFilter === 'female') {
          query = query.in('gender', ['Kadın', 'female']);
        } else if (genderFilter === 'other') {
          query = query.or('gender.is.null,gender.neq.Erkek,gender.neq.Kadın,gender.neq.male,gender.neq.female');
        }
      }

      // Search filter
      if (searchQuery && searchQuery.trim()) {
        const searchTerm = searchQuery.trim();
        query = query.or(`name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,id.ilike.%${searchTerm}%`);
      }

      // Get total count first with same filters
      let countQuery = supabase
        .from('users')
        .select('id', { count: 'exact', head: true });

      // Apply same filters to count query  
      if (statusFilter === 'active') {
        countQuery = countQuery.eq('is_active', true);
      } else if (statusFilter === 'inactive') {
        countQuery = countQuery.eq('is_active', false);
      }

      if (genderFilter !== 'all') {
        if (genderFilter === 'male') {
          countQuery = countQuery.in('gender', ['Erkek', 'male']);
        } else if (genderFilter === 'female') {
          countQuery = countQuery.in('gender', ['Kadın', 'female']);
        } else if (genderFilter === 'other') {
          countQuery = countQuery.or('gender.is.null,gender.neq.Erkek,gender.neq.Kadın,gender.neq.male,gender.neq.female');
        }
      }

      if (searchQuery && searchQuery.trim()) {
        const searchTerm = searchQuery.trim();
        countQuery = countQuery.or(`name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,id.ilike.%${searchTerm}%`);
      }

      const { count: totalCount, error: countError } = await countQuery;
      
      if (countError) {
        console.error('Error getting users count:', countError);
        throw countError;
      }

      // Get paginated data
      const { data, error } = await query
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        console.error('Error getting users:', error);
        throw error;
      }

      const users = (data || []).map(user => {
        // Birth date'den yaş hesapla
        let age = null;
        if (user.birth_date) {
          try {
            const birthDate = new Date(user.birth_date);
            const today = new Date();
            age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
              age--;
            }
          } catch (error) {
            console.warn('Birth date parse error for user:', user.id, error);
          }
        }

        // Photos array'ini parse et
        let parsedPhotos: string[] = [];
        try {
          if (Array.isArray(user.photos)) {
            parsedPhotos = user.photos.filter((p: string) => p && p.trim() !== '');
          } else if (typeof user.photos === 'string' && user.photos.trim()) {
            parsedPhotos = JSON.parse(user.photos).filter((p: string) => p && p.trim() !== '');
          }
        } catch (error) {
          console.warn('Photos parse error for user:', user.id, error);
        }

        // Profil resmi için fallback alanları
        const profilePicUrl = user.profile_pic_url || user.profilePicUrl || user.profile_picture_url || user.profilePictureUrl || null;
        const googlePhotoURL = user.google_photo_url || user.googlePhotoURL || user.photoURL || null;

        return {
          id: user.id,
          name: user.name || 'İsimsiz Kullanıcı',
          email: user.email,
          photos: parsedPhotos,
          // Ek profil resmi alanları
          profilePicUrl: profilePicUrl,
          googlePhotoURL: googlePhotoURL,
          photoURL: googlePhotoURL,
          isActive: user.is_active,
          gender: user.gender,
          age: age,
          interests: (() => {
            try {
              // Eğer zaten array ise, direkt kullan
              if (Array.isArray(user.interests)) return user.interests;
              // Eğer string ise, parse et
              if (typeof user.interests === 'string') return JSON.parse(user.interests);
              // Diğer durumlarda boş array
              return [];
            } catch (error) {
              console.warn('Interests parse error for user:', user.id, error);
              return [];
            }
          })(),
          bio: user.bio,
          city: user.city,
          languages: (() => {
            try {
              // Eğer zaten array ise, direkt kullan
              if (Array.isArray(user.languages)) return user.languages;
              // Eğer string ise, parse et
              if (typeof user.languages === 'string') return JSON.parse(user.languages);
              // Diğer durumlarda boş array
              return [];
            } catch (error) {
              console.warn('Languages parse error for user:', user.id, error);
              return [];
            }
          })(),
          relationshipStatus: user.relationship_status,
          createdAt: user.created_at,
          updatedAt: user.updated_at,
        };
      });

      console.log(`✅ SUPABASE: ${users.length} kullanıcı yüklendi (toplam: ${totalCount || 0})`);
      return { users, total: totalCount || 0 };
    } catch (error) {
      console.error('Error in getAllUsersWithPagination:', error);
      throw error;
    }
  },

  // Update user status (activate/deactivate)
  async updateUserStatus(userId: string, isActive: boolean): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('users')
        .update({
          is_active: isActive,
          updated_at: new Date().toISOString(),
        })
        .eq('id', userId);

      if (error) {
        console.error('Error updating user status:', error);
        throw error;
      }

      console.log(`✅ SUPABASE: Kullanıcı ${userId} durumu güncellendi: ${isActive ? 'Aktif' : 'Pasif'}`);
      return true;
    } catch (error) {
      console.error('Error in updateUserStatus:', error);
      throw error;
    }
  },

  // Get user by ID with full details
  async getUserById(userId: string): Promise<any | null> {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error getting user by ID:', error);
        throw error;
      }

      if (!data) {
        return null;
      }

      // Birth date'den yaş hesapla
      let age = null;
      if (data.birth_date) {
        try {
          const birthDate = new Date(data.birth_date);
          const today = new Date();
          age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
          }
        } catch (error) {
          console.warn('Birth date parse error for user:', data.id, error);
        }
      }

      const user = {
        id: data.id,
        name: data.name || 'İsimsiz Kullanıcı',
        email: data.email,
        photos: (() => {
          try {
            // Eğer zaten array ise, direkt kullan
            if (Array.isArray(data.photos)) return data.photos;
            // Eğer string ise, parse et
            if (typeof data.photos === 'string') return JSON.parse(data.photos);
            // Diğer durumlarda boş array
            return [];
          } catch (error) {
            console.warn('Photos parse error for user:', data.id, error);
            return [];
          }
        })(),
        isActive: data.is_active,
        gender: data.gender,
        age: age,
        interests: (() => {
          try {
            // Eğer zaten array ise, direkt kullan
            if (Array.isArray(data.interests)) return data.interests;
            // Eğer string ise, parse et
            if (typeof data.interests === 'string') return JSON.parse(data.interests);
            // Diğer durumlarda boş array
            return [];
          } catch (error) {
            console.warn('Interests parse error for user:', data.id, error);
            return [];
          }
        })(),
        bio: data.bio,
        city: data.city,
        languages: (() => {
          try {
            // Eğer zaten array ise, direkt kullan
            if (Array.isArray(data.languages)) return data.languages;
            // Eğer string ise, parse et
            if (typeof data.languages === 'string') return JSON.parse(data.languages);
            // Diğer durumlarda boş array
            return [];
          } catch (error) {
            console.warn('Languages parse error for user:', data.id, error);
            return [];
          }
        })(),
        relationshipStatus: data.relationship_status,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };

      console.log(`✅ SUPABASE: Kullanıcı ${userId} yüklendi`);
      return user;
    } catch (error) {
      console.error('Error in getUserById:', error);
      throw error;
    }
  },

  // Update user profile
  async updateUserProfile(userId: string, updates: any): Promise<boolean> {
    try {
      const updateData: any = {
        updated_at: new Date().toISOString(),
      };

      // Map the updates to database fields
      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.email !== undefined) updateData.email = updates.email;
      if (updates.bio !== undefined) updateData.bio = updates.bio;
      if (updates.gender !== undefined) updateData.gender = updates.gender;
      if (updates.city !== undefined) updateData.city = updates.city;
      if (updates.photos !== undefined) updateData.photos = JSON.stringify(updates.photos);
      if (updates.interests !== undefined) updateData.interests = JSON.stringify(updates.interests);
      if (updates.languages !== undefined) updateData.languages = JSON.stringify(updates.languages);
      if (updates.relationshipStatus !== undefined) updateData.relationship_status = updates.relationshipStatus;

      // 📍 LOCATION FIELD MAPPING - ÖNEMLİ!
      if (updates.location !== undefined) {
        updateData.location = updates.location; // JSON olarak direkt kaydet
        console.log('📍 Location güncelleniyor:', updates.location);
      }

      const { error } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', userId);

      if (error) {
        console.error('Error updating user profile:', error);
        throw error;
      }

      console.log(`✅ SUPABASE: Kullanıcı ${userId} profili güncellendi`);
      if (updates.location) {
        console.log(`📍 SUPABASE: Location güncellendi: ${JSON.stringify(updates.location)}`);
      }
      return true;
    } catch (error) {
      console.error('Error in updateUserProfile:', error);
      throw error;
    }
  },

  // Delete user (soft delete - set is_active to false)
  async deleteUser(userId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('users')
        .update({
          is_active: false,
          deleted_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', userId);

      if (error) {
        console.error('Error deleting user:', error);
        throw error;
      }

      console.log(`✅ SUPABASE: Kullanıcı ${userId} silindi (soft delete)`);
      return true;
    } catch (error) {
      console.error('Error in deleteUser:', error);
      throw error;
    }
  },

  // 🎁 GIFT CARD APPLICATIONS OPERATIONS

  // Get all gift card applications with user info
  async getAllGiftCardApplications({ 
    limit = 50, 
    offset = 0, 
    statusFilter 
  }: { 
    limit?: number; 
    offset?: number; 
    statusFilter?: string; 
  } = {}): Promise<any[]> {
    try {
      console.log('🔍 Supabase: Hediye kartı başvuruları alınıyor...');
      
      let query = supabase
        .from('gift_card_applications')
        .select('*')
        .order('application_date', { ascending: false })
        .range(offset, offset + limit - 1);

      // Status filter
      if (statusFilter && statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data: applicationsData, error } = await query;

      if (error) {
        console.error('❌ Supabase getAllGiftCardApplications error:', error);
        throw error;
      }

      if (!applicationsData || applicationsData.length === 0) {
        console.log('⚠️ No gift card applications found');
        return [];
      }

      // Get unique user IDs
      const userIds = Array.from(new Set(applicationsData.map(app => app.user_id).filter(Boolean)));
      
      // Fetch user details if we have user IDs
      let usersMap = new Map();
      if (userIds.length > 0) {
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select('id, name, email, total_tokens')
          .in('id', userIds);

        if (usersError) {
          console.warn('⚠️ Error fetching users for gift card applications:', usersError);
        } else if (usersData) {
          usersData.forEach(user => {
            usersMap.set(user.id, user);
          });
        }
      }

      // Combine applications with user data
      const result = applicationsData.map(app => {
        const user = usersMap.get(app.user_id);
        
        return {
          id: app.id,
          userId: app.user_id,
          userName: app.user_name,
          userEmail: app.user_email,
          giftCardType: app.gift_card_type,
          giftCardValue: app.gift_card_value,
          requiredTokens: app.required_tokens,
          recipientEmail: app.recipient_email,
          description: app.description,
          status: app.status,
          applicationDate: app.application_date,
          createdAt: app.created_at,
          adminNotes: app.admin_notes,
          approvedBy: app.approved_by,
          approvedAt: app.approved_at,
          sentAt: app.sent_at,
          giftCardCode: app.gift_card_code,
          userDetails: user ? {
            name: user.name || 'Unknown User',
            email: user.email || 'unknown@datezy.com',
            totalTokens: user.total_tokens || 0
          } : {
            name: app.user_name || 'Unknown User',
            email: app.user_email || 'unknown@datezy.com',
            totalTokens: 0
          }
        };
      });

      console.log('✅ Supabase gift card applications:', result.length);
      return result;
    } catch (error) {
      console.error('❌ Error in getAllGiftCardApplications:', error);
      throw error;
    }
  },

  // Update gift card application status
  async updateGiftCardApplicationStatus(
    applicationId: string, 
    status: 'approved' | 'rejected' | 'sent', 
    adminNotes?: string,
    adminId?: string
  ): Promise<boolean> {
    try {
      const updateData: any = {
        status,
        updated_at: new Date().toISOString(),
      };

      if (adminNotes) {
        updateData.admin_notes = adminNotes;
      }

      if (adminId) {
        updateData.approved_by = adminId;
      }

      if (status === 'approved') {
        updateData.approved_at = new Date().toISOString();
      } else if (status === 'sent') {
        updateData.sent_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('gift_card_applications')
        .update(updateData)
        .eq('id', applicationId);

      if (error) {
        console.error('❌ Error updating gift card application status:', error);
        throw error;
      }

      // If approved, deduct tokens from user
      if (status === 'approved') {
        try {
          const { data: appData, error: appError } = await supabase
            .from('gift_card_applications')
            .select('user_id, required_tokens')
            .eq('id', applicationId)
            .single();

          if (appError) {
            console.warn('⚠️ Could not get application data for token deduction:', appError);
          } else if (appData && appData.user_id && appData.required_tokens > 0) {
            // Get current user tokens
            const { data: userData, error: userError } = await supabase
              .from('users')
              .select('total_tokens')
              .eq('id', appData.user_id)
              .single();

            if (userError) {
              console.warn('⚠️ Could not get user data for token deduction:', userError);
            } else if (userData) {
              const currentTokens = userData.total_tokens || 0;
              const newTokens = Math.max(0, currentTokens - appData.required_tokens);
              
              const { error: updateTokenError } = await supabase
                .from('users')
                .update({ 
                  total_tokens: newTokens,
                  updated_at: new Date().toISOString()
                })
                .eq('id', appData.user_id);

              if (updateTokenError) {
                console.error('❌ Error updating user tokens:', updateTokenError);
              } else {
                console.log(`✅ User tokens updated: ${currentTokens} -> ${newTokens}`);
              }
            }
          }
        } catch (tokenError) {
          console.error('❌ Error in token deduction process:', tokenError);
        }
      }

      console.log('✅ Gift card application status updated:', applicationId);
      return true;
    } catch (error) {
      console.error('❌ Error in updateGiftCardApplicationStatus:', error);
      throw error;
    }
  },

  // Create gift card application (for testing)
  async createGiftCardApplication({
    userId,
    userName,
    userEmail,
    giftCardType,
    giftCardValue,
    requiredTokens,
    recipientEmail,
    description,
    status = 'pending'
  }: {
    userId: string;
    userName: string;
    userEmail: string;
    giftCardType: string;
    giftCardValue: number;
    requiredTokens: number;
    recipientEmail: string;
    description?: string;
    status?: string;
  }): Promise<boolean> {
    try {
      const applicationData = {
        user_id: userId,
        user_name: userName,
        user_email: userEmail,
        gift_card_type: giftCardType,
        gift_card_value: giftCardValue,
        required_tokens: requiredTokens,
        recipient_email: recipientEmail,
        description: description,
        status: status,
        application_date: new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('gift_card_applications')
        .insert(applicationData);

      if (error) {
        console.error('❌ Error creating gift card application:', error);
        throw error;
      }

      console.log('✅ Gift card application created');
      return true;
    } catch (error) {
      console.error('❌ Error in createGiftCardApplication:', error);
      throw error;
    }
  },

  // Delete gift card application
  async deleteGiftCardApplication(applicationId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('gift_card_applications')
        .delete()
        .eq('id', applicationId);

      if (error) {
        console.error('❌ Error deleting gift card application:', error);
        throw error;
      }

      console.log('✅ Gift card application deleted:', applicationId);
      return true;
    } catch (error) {
      console.error('❌ Error in deleteGiftCardApplication:', error);
      throw error;
    }
  },

  // Get gift card applications statistics
  async getGiftCardApplicationsStatistics(): Promise<{
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    sent: number;
  }> {
    try {
      const { data, error } = await supabase
        .from('gift_card_applications')
        .select('status');

      if (error) {
        console.error('❌ Error getting gift card applications statistics:', error);
        return {
          total: 0,
          pending: 0,
          approved: 0,
          rejected: 0,
          sent: 0,
        };
      }

      const stats = {
        total: data?.length || 0,
        pending: 0,
        approved: 0,
        rejected: 0,
        sent: 0,
      };

      if (data) {
        data.forEach(app => {
          const status = app.status || 'pending';
          if (stats.hasOwnProperty(status)) {
            stats[status as keyof typeof stats]++;
          }
        });
      }

      return stats;
    } catch (error) {
      console.error('❌ Error in getGiftCardApplicationsStatistics:', error);
      return {
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
        sent: 0,
      };
    }
  },

  // Create a new user
  async createUser(userData: any): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('users')
        .insert(userData);

      if (error) {
        console.error('Error creating user in Supabase:', error);
        return false;
      }

      console.log('✅ User created in Supabase:', userData.id);
      return true;
    } catch (error) {
      console.error('Error in createUser:', error);
      return false;
    }
  },

  // 35 Türk kadın kullanıcı oluştur (Supabase için)
  async create100TurkishWomen(
    progressCallback?: (current: number, total: number, message: string) => void
  ): Promise<{ success: boolean; createdCount: number; errorCount: number; message: string }> {
    try {
      console.log('🔄 SUPABASE: 35 Türk kadın kullanıcı oluşturuluyor...');
      progressCallback?.(0, 35, '🚀 SUPABASE: 35 Türk kadın kullanıcı oluşturma başlatıldı');

      const turkishFemaleNames = [
        'Ayşe', 'Fatma', 'Zeynep', 'Hatice', 'Merve', 'Elif', 'Esra', 'Dilek', 'Sevim', 'Gülhan',
        'Meryem', 'Gamze', 'Özlem', 'Döne', 'Sibel', 'Gonca', 'Nurten', 'Serpil', 'Selma', 'Hacer',
        'Seda', 'Sedef', 'Demet', 'Deniz', 'Nilgün', 'Cansu', 'Melike', 'Tuğba', 'Gizem', 'Hande',
        'Neslihan', 'Burcu', 'Pınar', 'Aslı', 'Begüm', 'Ece', 'Duygu', 'Ceylan', 'Ipek', 'Cemre',
        'Şule', 'Perihan', 'Yasemin', 'Fulya', 'Emel', 'Gül', 'Nevin', 'Sevgi', 'Meral', 'Gönül'
      ];

      const interests = [
        'Müzik', 'Kitap Okuma', 'Sinema', 'Seyahat', 'Yoga', 'Fotoğrafçılık', 'Yemek Yapma',
        'Dans', 'Resim', 'Spor', 'Doğa Yürüyüşü', 'Kahve', 'Sanat', 'Teknoloji', 'Moda',
        'Hayvanlar', 'Bahçıvanlık', 'Müze', 'Tiyatro', 'Alışveriş'
      ];

      const cities = [
        'İstanbul', 'Ankara', 'İzmir', 'Antalya', 'Bursa', 'Konya', 'Gaziantep', 'Adana',
        'Mersin', 'Kayseri', 'Eskişehir', 'Diyarbakır', 'Samsun', 'Denizli'
      ];

      const relationshipStatuses = ['Bekar', 'İlişkisi yok', 'Açık ilişkiye', 'Belirtmek istemiyorum'];
      const pets = ['Kedi', 'Köpek', 'Kuş', 'Balık', 'Yok'];

      // 📝 Doğal ve çeşitli bio şablonları
      const bioTemplates = [
        // Kısa ve öz
        (name: string, interest: string) => `${interest} tutkunu ✨`,
        (name: string, interest: string) => `Hayat güzel, keşfetmeye devam 🌸`,
        (name: string, interest: string) => `${interest} ve iyi sohbet 💫`,
        (name: string, interest: string) => `Pozitif enerji arıyorum`,
        (name: string, interest: string) => `Gerçek bağlantılar için buradayım`,
        
        // Orta uzunluk
        (name: string, interest: string) => `${interest} benim tutkum. Samimi insanlarla tanışmak istiyorum.`,
        (name: string, interest: string) => `Hayatın tadını çıkarmayı seven biri. ${interest} ile ilgileniyorum.`,
        (name: string, interest: string) => `Yeni insanlarla tanışmaya açığım. ${interest} severim.`,
        (name: string, interest: string) => `Sohbet etmeyi ve ${interest.toLowerCase()} yapmayı seviyorum.`,
        (name: string, interest: string) => `Eğlenceli ve samimi. ${interest} ile vakit geçirmeyi seviyorum.`,
        
        // Emoji ile
        (name: string, interest: string) => `☕ Kahve + iyi sohbet = mutluluk`,
        (name: string, interest: string) => `🎵 ${interest} + güzel anılar`,
        (name: string, interest: string) => `✈️ Keşfetmeyi seven biri`,
        (name: string, interest: string) => `📚 ${interest} tutkunu`,
        (name: string, interest: string) => `🌅 Hayatın güzel yanlarına odaklanıyorum`,
        
        // Sade
        (name: string, interest: string) => `${interest}`,
        (name: string, interest: string) => `Tanışalım`,
        (name: string, interest: string) => `Yeni başlangıçlar için buradayım`,
        (name: string, interest: string) => `Samimi sohbetler arıyorum`,
        (name: string, interest: string) => `Hayat paylaşınca güzel`,
        
        // Sorulu
        (name: string, interest: string) => `${interest} sever misin?`,
        (name: string, interest: string) => `Birlikte ${interest.toLowerCase()} yapalım mı?`,
        (name: string, interest: string) => `Kahve içmeye ne dersin?`,
        
        // Özgün
        (name: string, interest: string) => `Kitap kokusunu ve ${interest.toLowerCase()}'i seviyorum`,
        (name: string, interest: string) => `İyi müzik, iyi sohbet, iyi insanlar`,
        (name: string, interest: string) => `Basit şeylerden mutlu olabilen biri`,
        (name: string, interest: string) => `Gülümsemeyi seven biri arıyorum`,
        (name: string, interest: string) => `Hayatta kaliteyi tercih ediyorum`,
        (name: string, interest: string) => `Dürüstlük her şeyden önemli`,
        (name: string, interest: string) => `İçten ve samimi`,
      ];

      let createdCount = 0;
      let errorCount = 0;

      for (let i = 0; i < 35; i++) {
        try {
          // Rastgele veri oluştur
          const name = turkishFemaleNames[Math.floor(Math.random() * turkishFemaleNames.length)];
          const age = Math.floor(Math.random() * (38 - 25 + 1)) + 25; // 25-38 yaş
          const city = cities[Math.floor(Math.random() * cities.length)];
          
          // Doğum tarihi hesapla
          const birthDate = new Date();
          birthDate.setFullYear(birthDate.getFullYear() - age);

          // Rastgele ilgi alanları seç (2-4 adet)
          const userInterests = interests.sort(() => 0.5 - Math.random()).slice(0, Math.floor(Math.random() * 3) + 2);

          // Rastgele evcil hayvan
          const selectedPets = [pets[Math.floor(Math.random() * pets.length)]];

          // Konum bilgisi (İstanbul civarı)
          const latitude = 40.9 + (Math.random() * 0.2);
          const longitude = 28.8 + (Math.random() * 0.2);

          // Benzersiz ID oluştur
          const userId = `user_${Date.now()}_${i}_${Math.random().toString(36).substr(2, 9)}`;

          // 🆕 Benzersiz FCM token oluştur
          const generateUniqueFCMToken = (userId: string, index: number): string => {
            const randomId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
            const timestamp = Date.now().toString(36);
            const userIdPart = userId.substring(0, 8);
            return `fcm_${userIdPart}_${timestamp}_${randomId}:APA91bE${Math.random().toString(36).substring(2, 30)}`;
          };

          const fcmToken = generateUniqueFCMToken(userId, i);

          // 🖼️ Fake kadınlar için sabit profil resmi
          const profilePicUrl = FAKE_FEMALE_PROFILE_PIC_URL;

          // 📝 Rastgele ve doğal bio oluştur
          const randomBioTemplate = bioTemplates[Math.floor(Math.random() * bioTemplates.length)];
          const randomInterest = userInterests[Math.floor(Math.random() * userInterests.length)];
          const bio = randomBioTemplate(name, randomInterest);

          const userData = {
            id: userId,
            email: `test.kadın.${i + 1}@datezy.com`,
            name: name,
            birth_date: birthDate.toISOString(),
            gender: 'Kadın',
            interested_in: ['Erkek'], // JSON array
            bio: bio,
            city: city,
            interests: userInterests, // JSON array
            photos: [profilePicUrl],
            profile_pic_url: profilePicUrl,
            phone_number: null,
            relationship_status: relationshipStatuses[Math.floor(Math.random() * relationshipStatuses.length)],
            languages: ['Türkçe'], // JSON array
            location: {
              city: city,
              country: 'Türkiye',
              latitude: latitude,
              longitude: longitude,
              timestamp: Date.now()
            },
            occupation: null,
            education: null,
            height: null,
            social_links: null,
            pets: selectedPets, // JSON array
            is_active: true,
            is_online: false,
            is_profile_complete: true,
            is_system_user: false,
            last_active: new Date().toISOString(),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            total_tokens: 0,
            fcm_token: fcmToken, // 🆕 Benzersiz FCM token eklendi
            last_token_update: new Date().toISOString()
          };

          // Supabase'e kullanıcı ekle
          const { error } = await supabase
            .from('users')
            .insert(userData);

          if (error) {
            console.error(`❌ Kullanıcı ${i + 1} eklenemedi:`, error);
            errorCount++;
            progressCallback?.(i + 1, 35, `❌ Kullanıcı ${i + 1} (${name}) eklenemedi: ${error.message}`);
          } else {
            createdCount++;
            progressCallback?.(i + 1, 35, `✅ Kullanıcı ${i + 1} (${name}) - ${city}, ${age} yaş`);
          }

          // Rate limiting
          if (i % 10 === 9) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }

        } catch (error) {
          errorCount++;
          console.error(`❌ Kullanıcı ${i + 1} oluşturulurken hata:`, error);
          progressCallback?.(i + 1, 35, `❌ Kullanıcı ${i + 1} oluşturulamadı: ${error}`);
        }
      }

      const message = `${createdCount} kullanıcı başarıyla oluşturuldu, ${errorCount} hata oluştu.`;
      progressCallback?.(35, 35, `🎉 SUPABASE: Tamamlandı: ${message}`);

      return {
        success: true,
        createdCount,
        errorCount,
        message
      };

    } catch (error: any) {
      console.error('❌ SUPABASE: Kritik hata:', error);
      return {
        success: false,
        createdCount: 0,
        errorCount: 100,
        message: `Kritik hata: ${error.message}`
      };
    }
  },

  // 100 Türk erkek kullanıcı oluştur (Supabase için)
  async create100TurkishMen(
    progressCallback?: (current: number, total: number, message: string) => void
  ): Promise<{ success: boolean; createdCount: number; errorCount: number; message: string }> {
    try {
      console.log('🔄 SUPABASE: 100 Türk erkek kullanıcı oluşturuluyor...');
      progressCallback?.(0, 100, '🚀 SUPABASE: 100 Türk erkek kullanıcı oluşturma başlatıldı');

      const turkishMaleNames = [
        'Mehmet', 'Mustafa', 'Ali', 'Ahmet', 'Hüseyin', 'Hasan', 'İbrahim', 'İsmail', 'Ömer', 'Yusuf',
        'Murat', 'Emre', 'Burak', 'Cem', 'Serkan', 'Kemal', 'Oğuz', 'Tolga', 'Erkan', 'Selçuk',
        'Onur', 'Barış', 'Deniz', 'Kaan', 'Umut', 'Eren', 'Arda', 'Furkan', 'Gökhan', 'Hakan',
        'Özgür', 'Berk', 'Enes', 'Volkan', 'Sinan', 'Taner', 'Levent', 'Fatih', 'Cengiz', 'Bora',
        'Tarık', 'Recep', 'Yılmaz', 'Orhan', 'Metin', 'Kadir', 'Halil', 'Ramazan', 'Kerem', 'Alper'
      ];

      const interests = [
        'Futbol', 'Müzik', 'Teknoloji', 'Araba', 'Seyahat', 'Spor', 'Kitap Okuma', 'Sinema',
        'Fotoğrafçılık', 'Oyun', 'Balık Tutma', 'Dağcılık', 'Bisiklet', 'Motosiklet', 'Yüzme',
        'Bilim', 'Tarih', 'Kahve', 'Müze', 'Doğa', 'Fitness', 'Basketbol', 'Tenis', 'Golf'
      ];

      const cities = [
        'İstanbul', 'Ankara', 'İzmir', 'Antalya', 'Bursa', 'Konya', 'Gaziantep', 'Adana',
        'Mersin', 'Kayseri', 'Eskişehir', 'Diyarbakır', 'Samsun', 'Denizli'
      ];

      const relationshipStatuses = ['Bekar', 'İlişkisi yok', 'Açık ilişkiye', 'Belirtmek istemiyorum'];
      const pets = ['Kedi', 'Köpek', 'Kuş', 'Balık', 'Yok'];

      let createdCount = 0;
      let errorCount = 0;

      for (let i = 0; i < 100; i++) {
        try {
          // Rastgele veri oluştur
          const name = turkishMaleNames[Math.floor(Math.random() * turkishMaleNames.length)];
          const age = Math.floor(Math.random() * (38 - 25 + 1)) + 25; // 25-38 yaş
          const city = cities[Math.floor(Math.random() * cities.length)];
          
          // Doğum tarihi hesapla
          const birthDate = new Date();
          birthDate.setFullYear(birthDate.getFullYear() - age);

          // Rastgele ilgi alanları seç (2-4 adet)
          const userInterests = interests.sort(() => 0.5 - Math.random()).slice(0, Math.floor(Math.random() * 3) + 2);

          // Rastgele evcil hayvan
          const selectedPets = [pets[Math.floor(Math.random() * pets.length)]];

          // Konum bilgisi (İstanbul civarı)
          const latitude = 40.9 + (Math.random() * 0.2);
          const longitude = 28.8 + (Math.random() * 0.2);

          // Benzersiz ID oluştur
          const userId = `user_${Date.now()}_${i}_${Math.random().toString(36).substr(2, 9)}`;

          // 🆕 Benzersiz FCM token oluştur (erkek kullanıcılar için)
          const generateUniqueFCMTokenMale = (userId: string, index: number): string => {
            const randomId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
            const timestamp = Date.now().toString(36);
            const userIdPart = userId.substring(0, 8);
            return `fcm_${userIdPart}_${timestamp}_${randomId}:APA91bE${Math.random().toString(36).substring(2, 30)}`;
          };

          const fcmToken = generateUniqueFCMTokenMale(userId, i);

          const userData = {
            id: userId,
            email: `test.erkek.${i + 1}@datezy.com`,
            name: name,
            birth_date: birthDate.toISOString(),
            gender: 'Erkek',
            interested_in: ['Kadın'], // JSON array
            bio: `Selam! Ben ${name}. ${age} yaşındayım ve ${city}'da yaşıyorum. ${userInterests.slice(0, 2).join(' ve ')} ile ilgileniyorum.`,
            city: city,
            interests: userInterests, // JSON array
            photos: null,
            profile_pic_url: `https://xsgames.co/randomusers/assets/avatars/male/${(i % 78) + 1}.jpg`,
            phone_number: null,
            relationship_status: relationshipStatuses[Math.floor(Math.random() * relationshipStatuses.length)],
            languages: ['Türkçe'], // JSON array
            location: {
              city: city,
              country: 'Türkiye',
              latitude: latitude,
              longitude: longitude,
              timestamp: Date.now()
            },
            occupation: null,
            education: null,
            height: null,
            social_links: null,
            pets: selectedPets, // JSON array
            is_active: true,
            is_online: false,
            is_profile_complete: true,
            is_system_user: false,
            last_active: new Date().toISOString(),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            total_tokens: 0,
            fcm_token: fcmToken, // 🆕 Benzersiz FCM token eklendi
            last_token_update: new Date().toISOString()
          };

          // Supabase'e kullanıcı ekle
          const { error } = await supabase
            .from('users')
            .insert(userData);

          if (error) {
            console.error(`❌ Kullanıcı ${i + 1} eklenemedi:`, error);
            errorCount++;
            progressCallback?.(i + 1, 100, `❌ Kullanıcı ${i + 1} (${name}) eklenemedi: ${error.message}`);
          } else {
            createdCount++;
            progressCallback?.(i + 1, 100, `✅ Kullanıcı ${i + 1} (${name}) FCM token ile oluşturuldu - ${city}, ${age} yaş`);
          }

          // Rate limiting
          if (i % 10 === 9) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }

        } catch (error) {
          errorCount++;
          console.error(`❌ Kullanıcı ${i + 1} oluşturulurken hata:`, error);
          progressCallback?.(i + 1, 100, `❌ Kullanıcı ${i + 1} oluşturulamadı: ${error}`);
        }
      }

      const message = `${createdCount} erkek kullanıcı başarıyla oluşturuldu, ${errorCount} hata oluştu.`;
      progressCallback?.(100, 100, `🎉 SUPABASE: Tamamlandı: ${message}`);

      return {
        success: true,
        createdCount,
        errorCount,
        message
      };

    } catch (error: any) {
      console.error('❌ SUPABASE: Kritik hata:', error);
      return {
        success: false,
        createdCount: 0,
        errorCount: 100,
        message: `Kritik hata: ${error.message}`
      };
    }
  },

  // ============================================
  // 🗑️ HESAP SİLME BAŞVURULARI
  // ============================================

  // Tüm hesap silme başvurularını getir
  async getAccountDeletionRequests(): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('account_deletion_requests')
        .select('*')
        .order('requested_at', { ascending: false });

      if (error) {
        console.error('❌ Hesap silme başvuruları getirme hatası:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('❌ getAccountDeletionRequests hatası:', error);
      throw error;
    }
  },

  // Hesap silme başvurusunu onayla ve hesabı sil
  async approveAccountDeletion(requestId: string, userId: string, adminNotes: string): Promise<boolean> {
    try {
      console.log('🗑️ Hesap siliniyor:', userId);

      // 1. Firebase Authentication'dan kullanıcıyı sil (Cloud Function ile)
      try {
        const firebaseFunctionUrl = 'https://us-central1-datezy-production.cloudfunctions.net/deleteUserHttp';
        const response = await fetch(firebaseFunctionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userId }),
        });
        
        const result = await response.json();
        if (result.success) {
          console.log('✅ Firebase Auth\'dan silindi:', userId);
        } else {
          console.log('⚠️ Firebase silme hatası:', result.error);
        }
      } catch (firebaseError) {
        console.log('⚠️ Firebase silme hatası (devam ediliyor):', firebaseError);
      }

      // 2. Kullanıcıyı Supabase'den sil (tüm ilişkili verilerle birlikte)
      // Önce ilişkili verileri sil
      const tables = [
        { table: 'likes', column: 'sender_id' },
        { table: 'likes', column: 'receiver_id' },
        { table: 'user_swipes', column: 'swiper_id' },
        { table: 'user_swipes', column: 'swiped_id' },
        { table: 'matches', column: 'user1_id' },
        { table: 'matches', column: 'user2_id' },
        { table: 'notifications', column: 'user_id' },
        { table: 'super_likes', column: 'sender_id' },
        { table: 'super_likes', column: 'receiver_id' },
        { table: 'blocked_users', column: 'blocker_id' },
        { table: 'blocked_users', column: 'blocked_id' },
        { table: 'voice_calls', column: 'caller_id' },
        { table: 'voice_calls', column: 'receiver_id' },
        { table: 'posts', column: 'user_id' },
        { table: 'post_likes', column: 'user_id' },
        { table: 'post_comments', column: 'user_id' },
        { table: 'user_subscriptions', column: 'subscriber_id' },
        { table: 'user_subscriptions', column: 'subscribed_to_id' },
        { table: 'premium_subscriptions', column: 'user_id' },
        { table: 'premium_plus_subscriptions', column: 'user_id' },
        { table: 'profile_views', column: 'viewer_id' },
        { table: 'profile_views', column: 'viewed_id' },
        { table: 'user_points', column: 'user_id' },
        { table: 'points_transactions', column: 'user_id' },
        { table: 'sales', column: 'user_id' },
      ];

      for (const { table, column } of tables) {
        try {
          const { error } = await supabase.from(table).delete().eq(column, userId);
          if (error && !error.message.includes('does not exist')) {
            console.log(`⚠️ ${table}.${column} silme hatası:`, error.message);
          }
        } catch (e) {
          // Tablo yoksa devam et
        }
      }

      // 3. Ana kullanıcı kaydını sil
      const { error: userError } = await supabase
        .from('users')
        .delete()
        .eq('id', userId);

      if (userError) {
        console.error('❌ Kullanıcı silme hatası:', userError);
      }

      // 4. Başvuru durumunu güncelle
      const { error: updateError } = await supabase
        .from('account_deletion_requests')
        .update({
          status: 'completed',
          reviewed_at: new Date().toISOString(),
          reviewed_by: 'admin',
          admin_notes: adminNotes
        })
        .eq('id', requestId);

      if (updateError) {
        console.error('❌ Başvuru güncelleme hatası:', updateError);
        throw updateError;
      }

      console.log('✅ Hesap başarıyla silindi (Firebase + Supabase):', userId);
      return true;
    } catch (error) {
      console.error('❌ approveAccountDeletion hatası:', error);
      throw error;
    }
  },

  // Hesap silme başvurusunu reddet
  async rejectAccountDeletion(requestId: string, adminNotes: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('account_deletion_requests')
        .update({
          status: 'rejected',
          reviewed_at: new Date().toISOString(),
          reviewed_by: 'admin',
          admin_notes: adminNotes
        })
        .eq('id', requestId);

      if (error) {
        console.error('❌ Başvuru reddetme hatası:', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('❌ rejectAccountDeletion hatası:', error);
      throw error;
    }
  },
}; 